import React, { useState, useEffect } from 'react';
import { newsAPI } from '../lib/api';
import {
  Newspaper,
  ExternalLink,
  Clock,
  RefreshCw,
  Shield,
  AlertTriangle,
  TrendingUp,
  Database,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  Globe,
  Calendar,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Loader2,
  ShieldAlert,
  FileSearch,
  Building2,
  MapPin,
  Lock,
  BarChart3,
  Lightbulb,
  Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsPage = () => {
  // News state
  const [news, setNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsError, setNewsError] = useState(false);
  
  // KEV state
  const [kevData, setKevData] = useState(null);
  const [kevStats, setKevStats] = useState(null);
  const [kevLoading, setKevLoading] = useState(true);
  const [kevVendorFilter, setKevVendorFilter] = useState('');
  const [kevProductFilter, setKevProductFilter] = useState('');
  const [kevSearchTerm, setKevSearchTerm] = useState('');
  
  // Breach sources state
  const [breachSources, setBreachSources] = useState(null);
  const [sourcesLoading, setSourcesLoading] = useState(true);
  
  // NVD state
  const [nvdData, setNvdData] = useState(null);
  const [nvdLoading, setNvdLoading] = useState(false);
  const [nvdKeyword, setNvdKeyword] = useState('');
  
  // UI state
  const [activeTab, setActiveTab] = useState('news');
  const [expandedSections, setExpandedSections] = useState({
    kevStats: true,
    kevList: true,
    breachSources: true,
    monitoringTips: false
  });

  useEffect(() => {
    fetchNews();
    fetchKEVStats();
    fetchKEVData();
    fetchBreachSources();
  }, []);

  const fetchNews = async () => {
    setNewsLoading(true);
    setNewsError(false);
    try {
      const response = await newsAPI.getCybersecurityNews();
      let newsData = Array.isArray(response.data) ? response.data : [];
      newsData = newsData.sort((a, b) => {
        const aHasImage = a.imageUrl ? 1 : 0;
        const bHasImage = b.imageUrl ? 1 : 0;
        return bHasImage - aHasImage;
      });
      setNews(newsData);
    } catch (error) {
      console.error('Failed to fetch news:', error);
      setNewsError(true);
    } finally {
      setNewsLoading(false);
    }
  };

  const fetchKEVStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/vulnerabilities/kev/stats`);
      const data = await response.json();
      setKevStats(data);
    } catch (error) {
      console.error('Failed to fetch KEV stats:', error);
    }
  };

  const fetchKEVData = async (vendor = '', product = '') => {
    setKevLoading(true);
    try {
      let url = `${API_URL}/api/vulnerabilities/kev?limit=100`;
      if (vendor) url += `&vendor=${encodeURIComponent(vendor)}`;
      if (product) url += `&product=${encodeURIComponent(product)}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setKevData(data);
    } catch (error) {
      console.error('Failed to fetch KEV data:', error);
    } finally {
      setKevLoading(false);
    }
  };

  const fetchBreachSources = async () => {
    setSourcesLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/vulnerabilities/breach-sources`);
      const data = await response.json();
      setBreachSources(data);
    } catch (error) {
      console.error('Failed to fetch breach sources:', error);
    } finally {
      setSourcesLoading(false);
    }
  };

  const fetchNVDData = async () => {
    if (!nvdKeyword.trim()) return;
    setNvdLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/vulnerabilities/nvd/recent?keyword=${encodeURIComponent(nvdKeyword)}&limit=20`);
      const data = await response.json();
      setNvdData(data);
    } catch (error) {
      console.error('Failed to fetch NVD data:', error);
    } finally {
      setNvdLoading(false);
    }
  };

  const handleKEVFilter = () => {
    fetchKEVData(kevVendorFilter, kevProductFilter);
  };

  const clearKEVFilters = () => {
    setKevVendorFilter('');
    setKevProductFilter('');
    fetchKEVData();
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getSourceIcon = (iconName) => {
    const icons = {
      'shield-alert': ShieldAlert,
      'database': Database,
      'file-search': FileSearch,
      'hospital': Building2,
      'map-pin': MapPin,
      'lock': Lock,
      'bar-chart': BarChart3
    };
    return icons[iconName] || Globe;
  };

  const getSeverityColor = (severity) => {
    const colors = {
      'CRITICAL': 'bg-red-500/20 text-red-400 border-red-500/30',
      'HIGH': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'MEDIUM': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      'LOW': 'bg-green-500/20 text-green-400 border-green-500/30'
    };
    return colors[severity] || 'bg-slate-500/20 text-slate-400 border-slate-500/30';
  };

  const filteredKEV = kevData?.vulnerabilities?.filter(v => {
    if (!kevSearchTerm) return true;
    const search = kevSearchTerm.toLowerCase();
    return (
      v.cveID?.toLowerCase().includes(search) ||
      v.vendorProject?.toLowerCase().includes(search) ||
      v.product?.toLowerCase().includes(search) ||
      v.vulnerabilityName?.toLowerCase().includes(search)
    );
  }) || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <Newspaper className="w-8 h-8 text-violet-400" />
            Cybersecurity News & Threat Intelligence
          </h1>
          <p className="text-slate-400 mt-2">
            Stay informed with the latest cybersecurity news, vulnerabilities, and breach notifications
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
          <button
            onClick={() => setActiveTab('news')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'news'
                ? 'bg-violet-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Newspaper className="w-4 h-4" />
            News Feed
          </button>
          <button
            onClick={() => setActiveTab('kev')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'kev'
                ? 'bg-red-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <ShieldAlert className="w-4 h-4" />
            CISA KEV Catalog
          </button>
          <button
            onClick={() => setActiveTab('nvd')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'nvd'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Database className="w-4 h-4" />
            NVD Search
          </button>
          <button
            onClick={() => setActiveTab('sources')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              activeTab === 'sources'
                ? 'bg-emerald-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Globe className="w-4 h-4" />
            Breach Sources
          </button>
        </div>

        {/* News Feed Tab */}
        {activeTab === 'news' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-violet-400" />
                Latest Cybersecurity News
              </h2>
              <button
                onClick={fetchNews}
                disabled={newsLoading}
                className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors"
              >
                <RefreshCw className={`w-4 h-4 ${newsLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {newsLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
              </div>
            ) : newsError ? (
              <div className="text-center py-12 text-slate-400">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
                <p>Failed to load news. Please try again.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {news.map((item, index) => (
                  <a
                    key={index}
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden hover:border-violet-500/50 transition-all hover:scale-[1.02]"
                  >
                    {item.imageUrl && (
                      <div className="h-40 overflow-hidden">
                        <img
                          src={item.imageUrl}
                          alt={item.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-semibold text-white group-hover:text-violet-400 transition-colors line-clamp-2 mb-2">
                        {item.title}
                      </h3>
                      {item.description && (
                        <p className="text-sm text-slate-400 line-clamp-2 mb-3">
                          {item.description}
                        </p>
                      )}
                      <div className="flex items-center justify-between text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(item.pubDate).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1 text-violet-400">
                          Read more
                          <ExternalLink className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CISA KEV Tab */}
        {activeTab === 'kev' && (
          <div className="space-y-6">
            {/* KEV Stats */}
            {kevStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 text-center">
                  <div className="text-3xl font-bold text-white">{kevStats.totalVulnerabilities}</div>
                  <div className="text-sm text-slate-400">Total KEV Entries</div>
                </div>
                <div className="bg-red-500/10 rounded-xl border border-red-500/30 p-4 text-center">
                  <div className="text-3xl font-bold text-red-400">{kevStats.recentlyAdded}</div>
                  <div className="text-sm text-slate-400">Added (Last 30 Days)</div>
                </div>
                <div className="bg-amber-500/10 rounded-xl border border-amber-500/30 p-4 text-center">
                  <div className="text-3xl font-bold text-amber-400">{kevStats.dueSoon}</div>
                  <div className="text-sm text-slate-400">Due Soon (7 Days)</div>
                </div>
                <div className="bg-orange-500/10 rounded-xl border border-orange-500/30 p-4 text-center">
                  <div className="text-3xl font-bold text-orange-400">{kevStats.overdue}</div>
                  <div className="text-sm text-slate-400">Overdue</div>
                </div>
              </div>
            )}

            {/* Top Vendors */}
            {kevStats?.topVendors && (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="font-semibold text-white mb-3 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-violet-400" />
                  Most Affected Vendors
                </h3>
                <div className="flex flex-wrap gap-2">
                  {kevStats.topVendors.map((v, idx) => (
                    <button
                      key={idx}
                      onClick={() => { setKevVendorFilter(v.vendor); fetchKEVData(v.vendor, ''); }}
                      className="px-3 py-1.5 bg-slate-700 hover:bg-violet-600 text-slate-300 hover:text-white rounded-lg text-sm transition-colors"
                    >
                      {v.vendor} ({v.count})
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
              <div className="flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                  <label className="text-sm text-slate-400 mb-1 block">Search</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={kevSearchTerm}
                      onChange={(e) => setKevSearchTerm(e.target.value)}
                      placeholder="Search CVE, vendor, product..."
                      className="w-full pl-10 pr-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                    />
                  </div>
                </div>
                <div className="w-48">
                  <label className="text-sm text-slate-400 mb-1 block">Vendor</label>
                  <select
                    value={kevVendorFilter}
                    onChange={(e) => setKevVendorFilter(e.target.value)}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  >
                    <option value="">All Vendors</option>
                    {kevData?.filters?.vendors?.map((v, idx) => (
                      <option key={idx} value={v}>{v}</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={handleKEVFilter}
                  className="px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg"
                >
                  Apply Filters
                </button>
                {(kevVendorFilter || kevProductFilter) && (
                  <button
                    onClick={clearKEVFilters}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* KEV List */}
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
              <div className="p-4 border-b border-slate-700 flex items-center justify-between">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-red-400" />
                  Known Exploited Vulnerabilities
                  <span className="text-sm text-slate-400">({filteredKEV.length} results)</span>
                </h3>
                <a
                  href="https://www.cisa.gov/known-exploited-vulnerabilities-catalog"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-sm text-violet-400 hover:text-violet-300"
                >
                  View Full Catalog <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              
              {kevLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
                </div>
              ) : (
                <div className="max-h-[600px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="bg-slate-900/50 sticky top-0">
                      <tr className="text-left text-sm text-slate-400">
                        <th className="p-3">CVE ID</th>
                        <th className="p-3">Vendor / Product</th>
                        <th className="p-3">Vulnerability</th>
                        <th className="p-3">Date Added</th>
                        <th className="p-3">Due Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredKEV.map((vuln, idx) => {
                        const isOverdue = vuln.dueDate < new Date().toISOString().split('T')[0];
                        return (
                          <tr key={idx} className="border-t border-slate-700/50 hover:bg-slate-700/30">
                            <td className="p-3">
                              <a
                                href={`https://nvd.nist.gov/vuln/detail/${vuln.cveID}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono text-red-400 hover:text-red-300 hover:underline"
                              >
                                {vuln.cveID}
                              </a>
                            </td>
                            <td className="p-3">
                              <div className="text-white text-sm">{vuln.vendorProject}</div>
                              <div className="text-slate-400 text-xs">{vuln.product}</div>
                            </td>
                            <td className="p-3 max-w-xs">
                              <p className="text-slate-300 text-sm truncate" title={vuln.vulnerabilityName}>
                                {vuln.vulnerabilityName}
                              </p>
                            </td>
                            <td className="p-3 text-sm text-slate-400">{vuln.dateAdded}</td>
                            <td className="p-3">
                              <span className={`text-sm ${isOverdue ? 'text-red-400 font-semibold' : 'text-slate-400'}`}>
                                {vuln.dueDate}
                                {isOverdue && <span className="ml-1">⚠️</span>}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* NVD Search Tab */}
        {activeTab === 'nvd' && (
          <div className="space-y-6">
            <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-400" />
                NIST National Vulnerability Database Search
              </h3>
              <p className="text-slate-400 text-sm mb-4">
                Search the NVD for vulnerabilities by keyword (vendor, product, or CVE). Results show CVEs from the last 30 days.
              </p>
              
              <div className="flex gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={nvdKeyword}
                    onChange={(e) => setNvdKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && fetchNVDData()}
                    placeholder="Enter vendor, product, or CVE (e.g., Microsoft, Apache, Log4j)"
                    className="w-full px-4 py-3 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
                <button
                  onClick={fetchNVDData}
                  disabled={nvdLoading || !nvdKeyword.trim()}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-700 text-white rounded-lg flex items-center gap-2"
                >
                  {nvdLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Search NVD
                </button>
              </div>
            </div>

            {nvdData && (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
                <div className="p-4 border-b border-slate-700">
                  <h3 className="font-semibold text-white">
                    Search Results ({nvdData.count} of {nvdData.totalResults} total)
                  </h3>
                </div>
                <div className="max-h-[500px] overflow-y-auto">
                  {nvdData.vulnerabilities.map((vuln, idx) => (
                    <div key={idx} className="p-4 border-b border-slate-700/50 hover:bg-slate-700/30">
                      <div className="flex items-start justify-between mb-2">
                        <a
                          href={`https://nvd.nist.gov/vuln/detail/${vuln.cveId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="font-mono text-blue-400 hover:text-blue-300 hover:underline"
                        >
                          {vuln.cveId}
                        </a>
                        {vuln.cvssV3Score && (
                          <span className={`px-2 py-1 rounded text-xs font-semibold border ${getSeverityColor(vuln.cvssV3Severity)}`}>
                            CVSS {vuln.cvssV3Score} - {vuln.cvssV3Severity}
                          </span>
                        )}
                      </div>
                      <p className="text-slate-300 text-sm mb-2">{vuln.description}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span>Published: {new Date(vuln.published).toLocaleDateString()}</span>
                        <span>Modified: {new Date(vuln.lastModified).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Breach Sources Tab */}
        {activeTab === 'sources' && (
          <div className="space-y-6">
            {/* Monitoring Tips */}
            {breachSources?.monitoringTips && (
              <div className="bg-emerald-500/10 rounded-xl border border-emerald-500/30 p-4">
                <button
                  onClick={() => toggleSection('monitoringTips')}
                  className="w-full flex items-center justify-between"
                >
                  <h3 className="font-semibold text-emerald-300 flex items-center gap-2">
                    <Lightbulb className="w-5 h-5" />
                    Monitoring Best Practices
                  </h3>
                  <ChevronDown className={`w-5 h-5 text-emerald-400 transition-transform ${expandedSections.monitoringTips ? 'rotate-180' : ''}`} />
                </button>
                {expandedSections.monitoringTips && (
                  <ul className="mt-4 space-y-2">
                    {breachSources.monitoringTips.map((tip, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-300 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                        {tip}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* Sources List */}
            {sourcesLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {breachSources?.sources?.map((source, idx) => {
                  const IconComponent = getSourceIcon(source.icon);
                  const typeColors = {
                    vulnerability: 'border-red-500/30 bg-red-500/5',
                    breach: 'border-amber-500/30 bg-amber-500/5',
                    resource: 'border-blue-500/30 bg-blue-500/5',
                    report: 'border-purple-500/30 bg-purple-500/5'
                  };
                  
                  return (
                    <a
                      key={idx}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`p-4 rounded-xl border ${typeColors[source.type]} hover:scale-[1.02] transition-all group`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-slate-800 rounded-lg">
                          <IconComponent className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-white group-hover:text-emerald-400 transition-colors">
                            {source.name}
                          </h4>
                          <p className="text-sm text-slate-400 mt-1">{source.description}</p>
                          <div className="flex items-center gap-3 mt-3 text-xs">
                            <span className={`px-2 py-0.5 rounded capitalize ${
                              source.type === 'vulnerability' ? 'bg-red-500/20 text-red-400' :
                              source.type === 'breach' ? 'bg-amber-500/20 text-amber-400' :
                              source.type === 'resource' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-purple-500/20 text-purple-400'
                            }`}>
                              {source.type}
                            </span>
                            {source.state && (
                              <span className="text-slate-500">{source.state}</span>
                            )}
                            {source.sector && (
                              <span className="text-slate-500">{source.sector}</span>
                            )}
                            <span className="text-slate-500">{source.updateFrequency}</span>
                            {source.apiAvailable && (
                              <span className="bg-green-500/20 text-green-400 px-2 py-0.5 rounded">API</span>
                            )}
                          </div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-500 group-hover:text-emerald-400" />
                      </div>
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewsPage;
